import { Arrow, Content, Portal } from '@radix-ui/react-hover-card';
import React, { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

/** Above every layered surface in the app — sheets, modals and their overlays. */
const HOVER_CARD_Z_INDEX = 99999;

type HoverCardContentProps = {
  isShownTriangle?: boolean;
  triangleClassName?: string;
} & ComponentPropsWithoutRef<typeof Content>;

export const HoverCardContent = forwardRef<ElementRef<typeof Content>, HoverCardContentProps>(
  (
    {
      align = 'center',
      className,
      isShownTriangle = true,
      triangleClassName,
      sideOffset = 20,
      side,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <Portal>
        <Content
          align={align}
          className={twMerge(
            'relative rounded-md p-4 shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
          ref={ref}
          side={side}
          sideOffset={sideOffset}
          // Inline rather than a class: the content is portaled to the body and has to clear the
          // sheet overlay (z-50) and its panel (z-51). Callers routinely pass a `z-*` utility for
          // the trigger's own context, and twMerge would let that class win and sink the tooltip
          // behind the modal it was opened from.
          style={{ zIndex: HOVER_CARD_Z_INDEX, ...style }}
          {...props}
        >
          {children}
          {isShownTriangle && (
            <Arrow
              className={twMerge(
                'absolute -bottom-1 left-1/2 -ml-[6px] size-3 rotate-45 bg-[#101828]',
                triangleClassName,
              )}
            />
          )}
        </Content>
      </Portal>
    );
  },
);
HoverCardContent.displayName = Content.displayName;
